import React, { useMemo } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { View } from "@common/types/views/views";
import {
    AccrualMatchTableCallbacks,
    AccrualMatchSection,
    MatchableItem,
    MatchItemType,
    PaymentAmounts,
} from "../types";
import sharedStyles from "../AccrualMatchTable.module.scss";
import {
    createAllocationViewConfig,
    createResolveDifferenceViewConfig,
} from "./allocationViewConfig";

export interface AllocationTableOptions {
    section: AccrualMatchSection;
    paymentAmounts?: PaymentAmounts;
    accounts?: Account[];
    entityId?: number;
    accountsByEntityId?: Record<number, Account[]>;
    showAccountNumbers?: boolean;
    amountToBeAllocated?: number;
    // Selected matched items, used by the source TRANSACTION row to derive an
    // aggregated Accrual ledger account (showing "Multiple accounts" when
    // matches target different accounts).
    selectedItems?: MatchableItem[];
}

interface Props {
    readonly data: MatchableItem[];
    readonly section: AccrualMatchSection;
    readonly view: View;
    readonly onViewChange: (view: View) => void;
    readonly paymentAmounts?: PaymentAmounts;
    readonly accounts?: Account[];
    readonly entityId?: number;
    readonly accountsByEntityId?: Record<number, Account[]>;
    readonly showAccountNumbers?: boolean;
    readonly amountToBeAllocated?: number;
    readonly selectedItems?: MatchableItem[];
    readonly onPaymentChange?: (id: string, value: number, max: number) => void;
    readonly onRemainingAccountChange?: (
        id: string,
        accountId: string | null,
    ) => void;
    readonly onRemainingCashAccountChange?: (
        id: string,
        accountId: string | null,
    ) => void;
    readonly onRemainingCategoryChange?: (
        id: string,
        categoryId: string | null,
    ) => void;
    readonly onRemainingAmountChange?: (id: string, amount: number) => void;
    readonly onRemainingDescriptionChange?: (
        id: string,
        description: string,
    ) => void;
    readonly onRemoveRow?: (id: string) => void;
    readonly matchItemType?: MatchItemType | null;
}

export const AllocationTable: React.FC<Props> = ({
    data,
    section,
    view,
    onViewChange,
    paymentAmounts,
    accounts,
    entityId,
    accountsByEntityId,
    showAccountNumbers,
    amountToBeAllocated,
    selectedItems,
    onPaymentChange,
    onRemainingAccountChange,
    onRemainingCashAccountChange,
    onRemainingCategoryChange,
    onRemainingAmountChange,
    onRemainingDescriptionChange,
    onRemoveRow,
    matchItemType = null,
}) => {
    const config = useMemo(
        () =>
            section === "remaining"
                ? createResolveDifferenceViewConfig(matchItemType)
                : createAllocationViewConfig(matchItemType),
        [matchItemType, section],
    );
    const tableMeta = useMemo<
        ViewTableMeta<AccrualMatchTableCallbacks, AllocationTableOptions>
    >(
        () => ({
            callbacks: {
                onPaymentChange,
                onRemainingAccountChange,
                onRemainingCashAccountChange,
                onRemainingCategoryChange,
                onRemainingAmountChange,
                onRemainingDescriptionChange,
                onRemoveRow,
            },
            options: {
                section,
                paymentAmounts,
                accounts,
                entityId,
                accountsByEntityId,
                showAccountNumbers,
                amountToBeAllocated,
                selectedItems,
            },
        }),
        [
            onPaymentChange,
            onRemainingAccountChange,
            onRemainingCashAccountChange,
            onRemainingCategoryChange,
            onRemainingAmountChange,
            onRemainingDescriptionChange,
            onRemoveRow,
            section,
            paymentAmounts,
            accounts,
            entityId,
            accountsByEntityId,
            showAccountNumbers,
            amountToBeAllocated,
            selectedItems,
        ],
    );

    return (
        <ViewTable
            className={sharedStyles.rightAlignedAmounts}
            data={data}
            view={view}
            onViewChange={onViewChange}
            viewChangeDebounceMs={0}
            config={config}
            tableMeta={tableMeta}
            autoExpandOnMount
            rowBorders
            hideTopBorder={section === "source"}
        />
    );
};
